import React, { useEffect, useRef } from "react";
import L, { Control } from "leaflet";
import { useMap } from "react-leaflet";
import colormap from "colormap";
import { ColorControlPoint } from "../types/ColorControlPoint";
import { ColorContourResponse } from "../types/ColorContourRepsonse";
interface ColorBarLegendProps {
  min: number;
  max: number;
  colormapName: ColorContourResponse;
  units: string;
}

const ColorBarLegend: React.FC<ColorBarLegendProps> = ({ min, max, colormapName, units }) => {
  const map = useMap();
  const controlRef = useRef<Control | null>(null);

  useEffect(() => {
    const container = L.DomUtil.create("div", "leaflet-control colorbar-legend");
    container.style.background = "white";
    container.style.padding = "8px";
    container.style.borderRadius = "8px";
    container.style.boxShadow = "0 1px 4px rgba(0,0,0,0.2)";
    container.style.textAlign = "center";

    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 12;
    const ctx = canvas.getContext("2d");

    const colors = colormap({
      colormap: colormapName.colors,
      nshades: 256,
      format: "rgba",
      alpha: 1,
    });

    if (ctx) {
      for (let i = 0; i < 256; i++) {
        const c = colors[i];
        ctx.fillStyle = `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${c[3]})`;
        ctx.fillRect(i, 0, 1, canvas.height);
      }
    }
    const labelContainer = document.createElement("div");
    labelContainer.style.position = "relative";
    labelContainer.style.height = "16px";
    labelContainer.style.marginTop = "4px";
    labelContainer.style.width = canvas.width + "px";
    const numTicks = 5;
    for (let i = 0; i < numTicks; i++) {
      const value = min + ((max - min) * i) / (numTicks - 1);
      const percent = (i / (numTicks - 1)) * 100;

      const tick = document.createElement("div");
      tick.style.position = "absolute";
      tick.style.left = `${percent}%`;
      tick.style.transform = "translateX(-50%)";
      tick.style.fontSize = "12px";
      tick.style.whiteSpace = "nowrap";
      tick.innerText = value.toFixed(0);

      labelContainer.appendChild(tick);
    }

    const unitLabel = document.createElement("div");
    unitLabel.style.textAlign = "center";
    unitLabel.style.fontSize = "12px";
    unitLabel.style.marginTop = "2px";
    unitLabel.textContent = units; 

    container.appendChild(canvas);
    container.appendChild(labelContainer);
    container.appendChild(unitLabel);

    // ✅ Fix control creation with type assertion
    const legendControl = new L.Control({ position: "bottomright" });
    legendControl.onAdd = () => container;
    legendControl.addTo(map);
    controlRef.current = legendControl;

    return () => {
      if (controlRef.current) {
        controlRef.current.remove();
        controlRef.current = null;
      }
    };
  }, [map, min, max, colormapName,units]);


  
// ✅ Why return null?
// This is a React component, but it doesn't render anything visually in the React tree. Instead, it imperatively adds a Leaflet 
// legend control to the map using the Leaflet API (legendControl.addTo(map)), and manages cleanup when dependencies change or the component unmounts.

// So:

// return null means “don’t render any DOM”

// All the actual UI (the legend) is managed inside Leaflet, not in JSX

  return null;
};

export default ColorBarLegend;
